import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  CheckCircle,
  PlayCircle,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import { Button } from "@/components/ui/button";
import ConfirmationDialog from "@/components/ConfirmationDialog/ConfirmationDialog";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/utils/utils";
import { MyCoursesItem } from "@/modules/learningCenter/features/CourseDirectory/types/viewer";
import { formatProgressPercent } from "@/modules/learningCenter/features/CourseDirectory/utils/progress";
import {
  MY_COURSES_TABS,
  MyCoursesTab,
  useMyCourses,
} from "@/modules/learningCenter/features/MyCourses/hooks/useMyCourses/useMyCourses";

// Status badge config: label, icon, and a semantic color per enrollment state.
const STATUS_CONFIG: Record<
  MyCoursesItem["status"],
  { label: string; icon: LucideIcon; className: string }
> = {
  enrolled: {
    label: "Enrolled",
    icon: PlayCircle,
    className:
      "bg-slate-100 text-slate-700 dark:bg-slate-700 dark:text-slate-200",
  },
  in_progress: {
    label: "In Progress",
    icon: PlayCircle,
    className: "bg-sky-100 text-sky-700 dark:bg-sky-900/40 dark:text-sky-200",
  },
  completed: {
    label: "Completed",
    icon: CheckCircle,
    className:
      "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200",
  },
  cancelled: {
    label: "Cancelled",
    icon: XCircle,
    className:
      "bg-rose-100 text-rose-700 dark:bg-rose-900/40 dark:text-rose-200",
  },
};

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function StatusBadge({ status }: { status: MyCoursesItem["status"] }) {
  const config = STATUS_CONFIG[status];
  const Icon = config.icon;
  return (
    <span
      className={cn(
        "inline-flex flex-shrink-0 items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium",
        config.className,
      )}
    >
      <Icon className="h-3 w-3" />
      {config.label}
    </span>
  );
}

export function MyCourses() {
  const navigate = useNavigate();
  const { filteredItems, tab, setTab, isLoading, cancel, reenroll } =
    useMyCourses();

  const [confirmCancel, setConfirmCancel] = useState<MyCoursesItem | null>(
    null,
  );

  const goToCourse = (item: MyCoursesItem) => {
    if (item.lastAccessedModuleUuid) {
      navigate(
        `/hub/learning-center/courses/${item.courseUuid}/modules/${item.lastAccessedModuleUuid}`,
      );
      return;
    }
    navigate(`/hub/learning-center/courses/${item.courseUuid}`);
  };

  return (
    <MainPageWrapper
      subtitle="Your enrollments and progress"
      title="My Courses"
    >
      <Tabs
        onValueChange={(value) => setTab(value as MyCoursesTab)}
        value={tab}
      >
        <TabsList>
          {MY_COURSES_TABS.map((t) => (
            <TabsTrigger key={t.value} value={t.value}>
              {t.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      <div className="mt-6">
        {isLoading ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            Loading…
          </p>
        ) : filteredItems.length === 0 ? (
          <p className="py-8 text-center text-sm text-gray-500 dark:text-gray-400">
            No courses to show.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
            {filteredItems.map((item) => {
              const percent = formatProgressPercent(item.progressPercentage);
              const cancelled = item.status === "cancelled";
              return (
                <div
                  className={cn(
                    "flex flex-col rounded-lg border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4",
                    cancelled && "opacity-60",
                  )}
                  key={item.enrollmentUuid}
                >
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="min-w-0 text-sm font-medium text-gray-900 dark:text-white">
                      {item.courseTitle}
                    </h3>
                    <StatusBadge status={item.status} />
                  </div>

                  {item.tagline && (
                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400 line-clamp-2">
                      {item.tagline}
                    </p>
                  )}

                  {/* Progress */}
                  {!cancelled && (
                    <div className="mt-3">
                      <div className="flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
                        <span>
                          {item.completedModuleCount} of {item.moduleCount}{" "}
                          modules
                        </span>
                        <span>{percent}%</span>
                      </div>
                      <div className="mt-1.5 h-1.5 w-full overflow-hidden rounded-full bg-gray-100 dark:bg-slate-700">
                        <div
                          className="h-full rounded-full bg-primary"
                          style={{ width: `${percent}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Dates */}
                  <div className="mt-3 space-y-1 text-xs text-gray-500 dark:text-gray-400">
                    {item.enrolledAt && (
                      <div className="flex items-center gap-1.5">
                        <Calendar className="h-3.5 w-3.5" />
                        <span>Enrolled {formatDate(item.enrolledAt)}</span>
                      </div>
                    )}
                    {item.completedAt && (
                      <div className="flex items-center gap-1.5">
                        <CheckCircle className="h-3.5 w-3.5" />
                        <span>Completed {formatDate(item.completedAt)}</span>
                      </div>
                    )}
                    {cancelled && (
                      <div className="flex items-center gap-1.5">
                        <XCircle className="h-3.5 w-3.5" />
                        <span>Cancelled</span>
                      </div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="mt-4 flex gap-2 pt-1">
                    {cancelled ? (
                      <Button
                        className="flex-1"
                        onClick={() => void reenroll(item.enrollmentUuid)}
                      >
                        Re-enroll
                      </Button>
                    ) : (
                      <>
                        <Button
                          className="flex-1"
                          onClick={() => goToCourse(item)}
                        >
                          Continue
                        </Button>
                        <Button
                          onClick={() => setConfirmCancel(item)}
                          variant="outline"
                        >
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <ConfirmationDialog
        cancelMessage="Keep enrollment"
        confirmMessage="Cancel enrollment"
        dialogInstructionFinalQuestion="Are you sure you want to cancel?"
        dialogInstructionItems={[
          "Your progress is preserved.",
          "You can re-enroll later from this page.",
        ]}
        dialogInstructionTitle="Cancelling this enrollment will:"
        dialogTitleContent="Cancel enrollment"
        isOpen={confirmCancel !== null}
        onClose={() => setConfirmCancel(null)}
        onConfirm={() => {
          if (confirmCancel) {
            void cancel(confirmCancel.enrollmentUuid);
          }
          setConfirmCancel(null);
        }}
      />
    </MainPageWrapper>
  );
}

export default MyCourses;
